import { Button } from 'antd';
import { Helmet } from 'react-helmet-async';
import { useNavigate } from 'react-router-dom';
import { SEO } from '../../components/common/SEO';
import { MarketingLayout } from './MarketingLayout';

interface FaqItem {
  q: string;
  a: string;
}

interface FaqGroup {
  title: string;
  items: FaqItem[];
}

const faqGroups: FaqGroup[] = [
  {
    title: 'Pricing and billing',
    items: [
      {
        q: 'How much does Rongeka Africa cost?',
        a: 'Rongeka costs $0.50 per student per month after your free term. A school with 100 students pays $50 per month, and a school with 300 students pays $150 per month. Add-on modules are billed separately at their own monthly rates.',
      },
      {
        q: 'Is the free trial really free?',
        a: 'Yes. Every new school gets one complete school term free, with full platform access. There is no credit card, no demo call, and billing only starts if you choose to continue.',
      },
      {
        q: 'Are there setup fees or contracts?',
        a: 'No. There are no setup fees, no onboarding charges, and no lock-in contracts. You can stop at the end of any month.',
      },
      {
        q: 'What is included in the core platform?',
        a: 'Student records, enrollment and guardians, attendance with parent alerts, fees and invoicing, assessments and report cards, bulk WhatsApp and email messaging, student ID cards, and financial reports - all in the $0.50 per student price.',
      },
    ],
  },
  {
    title: 'WhatsApp',
    items: [
      {
        q: 'Do parents and teachers need to download an app?',
        a: 'No. Parents, teachers, students, and drivers use Rongeka through WhatsApp. One Hi message and they are connected - no downloads, no passwords.',
      },
      {
        q: 'What can parents do on WhatsApp?',
        a: 'Check fee balances and invoices, receive attendance alerts, view results and report cards, and message the school directly.',
      },
      {
        q: 'What can teachers do on WhatsApp?',
        a: 'Mark attendance, view their classes and timetable, and access grades without opening a computer.',
      },
    ],
  },
  {
    title: 'Getting started',
    items: [
      {
        q: 'How long does it take to set up?',
        a: 'Most schools are fully set up within one day. Import your student list, add staff, and configure classes and fee structures - Rongeka is cloud-based, so there is nothing to install.',
      },
      {
        q: 'Can we import our existing student records?',
        a: 'Yes. Student lists import in bulk from a spreadsheet, so you do not have to capture students one by one.',
      },
      {
        q: 'Which countries does Rongeka support?',
        a: 'Rongeka is built for Zimbabwean schools first and is used by schools in Zimbabwe, South Africa, Botswana, and Zambia.',
      },
    ],
  },
  {
    title: 'Fees and payments',
    items: [
      {
        q: 'Can parents pay school fees with EcoCash?',
        a: 'Yes. Online payments are processed through Pesepay, so parents can pay with EcoCash or a bank card and the payment is receipted against the student account automatically. Ask us to switch online payments on for your school.',
      },
      {
        q: 'How do fee reminders work?',
        a: 'Rongeka sends automated payment reminders to guardians on WhatsApp and email on a schedule you control, and parents can check their balance themselves at any time.',
      },
      {
        q: 'Can Rongeka track arrears from previous terms?',
        a: 'Yes. Each student account keeps current-term fees and carried-over arrears separate, so you always know exactly who owes what.',
      },
    ],
  },
  {
    title: 'Accounting and report cards',
    items: [
      {
        q: 'Can Rongeka replace Sage Pastel or QuickBooks?',
        a: 'Yes. The accounting add-on covers double-entry bookkeeping, trial balance, income statement, balance sheet, bank reconciliation, budgets, and year-end reports - with fee income posting into your books automatically.',
      },
      {
        q: 'Does Rongeka support ZIMSEC and Cambridge grading?',
        a: 'Yes. Grading scales are configurable to match ZIMSEC or Cambridge structures, and end-of-term report cards are delivered to parents on WhatsApp.',
      },
    ],
  },
  {
    title: 'Data and security',
    items: [
      {
        q: 'Is our school data secure?',
        a: 'Rongeka runs in the cloud over encrypted connections, with role-based access so each staff member only sees what their role allows.',
      },
    ],
  },
];

const faqJsonLd = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: faqGroups
    .flatMap((group) => group.items)
    .map(({ q, a }) => ({
      '@type': 'Question',
      name: q,
      acceptedAnswer: { '@type': 'Answer', text: a },
    })),
};

/**
 * /faq - dedicated, prerendered FAQ page targeting long-tail question
 * searches. The visible Q&A and the FAQPage JSON-LD are built from the same
 * data so they can never drift apart.
 */
export const FaqPage: React.FC = () => {
  const navigate = useNavigate();

  return (
    <MarketingLayout current="/faq">
      <SEO
        title="Frequently Asked Questions | Rongeka Africa School Management System"
        description="Straight answers about Rongeka Africa: pricing and the free term, WhatsApp for parents and teachers, EcoCash fee payments, school accounting, and ZIMSEC report cards."
        url="https://rongeka.africa/faq"
        keywords="school management system cost Zimbabwe, pay school fees EcoCash, school WhatsApp system, ZIMSEC report cards software, school accounting software questions"
      />
      <Helmet>
        <script type="application/ld+json">{JSON.stringify(faqJsonLd)}</script>
      </Helmet>

      <header className="mkt-hero">
        <div className="landing-section-label">FAQ</div>
        <h1 className="mkt-hero-title">Frequently Asked Questions</h1>
        <p className="mkt-hero-lead">
          Straight answers about pricing, WhatsApp, fees, accounting, and getting started.
          Can&apos;t find yours? Message us on WhatsApp and a real person will answer.
        </p>
        <div className="mkt-hero-actions">
          <Button type="primary" size="large" onClick={() => navigate('/get-started')}>
            Start Your Free Term
          </Button>
        </div>
      </header>

      {faqGroups.map((group, index) => (
        <section
          key={group.title}
          className={index % 2 === 1 ? 'mkt-section mkt-section-alt' : 'mkt-section'}
        >
          <div className="mkt-content">
            <h2 className="mkt-section-heading">{group.title}</h2>
            <div className="mkt-faq-list">
              {group.items.map((item) => (
                <div className="mkt-faq-item" key={item.q}>
                  <h3>{item.q}</h3>
                  <p>{item.a}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      ))}
    </MarketingLayout>
  );
};
